import React, { useEffect, useState } from "react";
import DailyUserReponse from "../../api/models/statistics/DailyUserRespone";
import UserInfoApi, { UserCountByLabLocation } from "../../api/services/UserInfoApi";
import CustomTable from "../../components/CustomTable";
import useEnv from "../../hooks/env";
import DateRange from "./DateRange.model";

const LAB_USER_CATEGORIES = ["Lab Users EU", "Lab Users Non-EU", "Non-Lab Users", "Unknown", "In Total"] as const;

const COLUMN_HEADERS = ["Date"].concat(LAB_USER_CATEGORIES);

type DailyUserActivityResponse = Omit<TestRequestRaw.Schemas.DailyUserActivityResponse, "categorized"> & {
  categorized: UserCountByLabLocation;
};
type DailyUsers = Omit<DailyUserReponse, "activities" | "averageCategorized"> & {
  activities: DailyUserActivityResponse[];
  averageCategorized: UserCountByLabLocation;
};

type LabUserCategories = typeof LAB_USER_CATEGORIES[number];

type UserCountByCategory = {
  [C in LabUserCategories]: number;
};

type ActiveUsersCategorizedRow = {
  [C in LabUserCategories]: number;
} & { date: string };

const mapCategorized = (count: UserCountByLabLocation): Omit<UserCountByCategory, "In Total"> => {
  const { EUROPEAN_UNION, NON_EUROPEAN_UNION, MIXED, NONE, _UNDEFINED } = count;
  return {
    "Lab Users EU": EUROPEAN_UNION,
    "Non-Lab Users": MIXED + NONE,
    "Lab Users Non-EU": NON_EUROPEAN_UNION,
    Unknown: _UNDEFINED,
  };
};

const createUserCategorizedRows = (dailyUserData: DailyUsers): ActiveUsersCategorizedRow[] => {
  const userActivities = dailyUserData.activities.map((a) => {
    const { date, categorized, total } = a;
    return {
      date,
      ...mapCategorized(categorized),
      "In Total": total,
    };
  });

  return [
    ...userActivities,
    {
      date: "Average",
      ...mapCategorized(dailyUserData.averageCategorized),
      "In Total": dailyUserData.average,
    },
  ];
};

interface ActiveUsersTableProps {
  activeUserDays: number;
  dateRange: DateRange<string> | null;
}

const ActiveUsersTable: React.FC<ActiveUsersTableProps> = ({ dateRange }) => {
  const [activeUsersCategorizedRows, setActiveUsersCategorizedRows] = useState<ActiveUsersCategorizedRow[]>([]);
  const { api } = useEnv();
  const userInfoApi = new UserInfoApi(api);

  const fetchActiveUsersDateRange = (range: DateRange<string>) => {
    userInfoApi.getActiveUsersDateRange(range).then(({ data }) => {
      const rows = createUserCategorizedRows(data as DailyUsers);
      setActiveUsersCategorizedRows(rows);
    });
  };

  useEffect(() => {
    if (dateRange) {
      fetchActiveUsersDateRange(dateRange);
    }
  }, [dateRange]);

  return (
    <>
      <CustomTable boldIndex={0} columns={COLUMN_HEADERS} headDense rows={activeUsersCategorizedRows} />
    </>
  );
};

export default ActiveUsersTable;
